import styled from "@emotion/styled";
import React from "react";
import useOpen from "../../../hooks/useOpen";
import { Loading } from "../../../Loading";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { AppPatientsState, Patient } from "../../../system/AppState/AppPatientsState";
import { translate } from "../../../translate";
import { getIsRN } from "../../../utils/caregiverUtils";
import AddressDialog from "../../Auth/components/AddressDialog";
import AsyncImage from "../../Generic/AsyncImage";
import ScaffoldSearchbar from "../../ScaffoldSearchbar";
import SearchPatientDialog from "../../SearchPatientDialog";
import useMainLayoutPreference from "../hooks/useMainLayoutPreference";
import { getCaregiverPhotoUrl } from "../main-utils";
import NotificationsButton from "./NotificationsButton";
import SortPreferenceButton from "./SortPreferenceButton";

export type DefinedPlaceResult = google.maps.places.PlaceResult & {
  geometry: google.maps.places.PlaceGeometry;
  formatted_address: string;
};

type Props = {
  caregiver: Pick<AppCaregiver, "photoUrl" | "certifications">;
  patientsState: Loading | AppPatientsState;
  notificationsBadge: number;
  filteredPatient: Patient | null;
  onSearchPlace: (place: DefinedPlaceResult) => void;
  onSearchPatient: (patient: Patient | null) => void;
  onClickAvatar: () => void;
  onClickNotifications: () => void;
};

export default function MainNavbar(props: Props) {
  const [mainLayout] = useMainLayoutPreference();

  const avatarNode = (
    <Avatar src={getCaregiverPhotoUrl(props.caregiver)} onClick={props.onClickAvatar} />
  );

  const notificationsNode = (
    <NotificationsButton badge={props.notificationsBadge} onClick={props.onClickNotifications} />
  );

  switch (mainLayout) {
    case "Map":
      return (
        <Root>
          {avatarNode}
          <Searchbar notificationsNode={notificationsNode} {...props} />
        </Root>
      );

    case "Cards":
      return (
        <Root>
          {avatarNode}
          <TitleContainer>
            {/* <AppIcon icon={MEDFLYT_LOGO_TEXT_ONLY} fill="primary" /> */}
          </TitleContainer>
          <NavbarActionButtons>
            <SortPreferenceButton />
            {notificationsNode}
          </NavbarActionButtons>
        </Root>
      );
  }
}

function Searchbar(props: Props & { notificationsNode: React.ReactNode }) {
  const isRN = React.useMemo(
    () => getIsRN(props.caregiver.certifications),
    [props.caregiver.certifications]
  );

  return isRN ? <PatientSearchbar {...props} /> : <AddressSearchbar {...props} />;
}

function AddressSearchbar(props: Props & { notificationsNode: React.ReactNode }) {
  const addressDialog = useOpen();

  const handleClickPlace = (place: google.maps.places.PlaceResult) => {
    if (place.geometry === undefined || place.formatted_address === undefined) {
      return;
    }

    props.onSearchPlace({
      ...place,
      geometry: place.geometry,
      formatted_address: place.formatted_address,
    });

    addressDialog.actions.dismiss();
  };

  return (
    <>
      <ScaffoldSearchbar
        placeholder={translate(STRINGS.SEARCH_NEARY_VISITS)}
        trailing={props.notificationsNode}
        onClick={addressDialog.actions.open}
        onFocus={addressDialog.actions.open}
      />

      <AddressDialog
        isOpen={addressDialog.isOpen}
        placeholder={translate(STRINGS.SEARCH_NEARY_VISITS)}
        onClickPlace={handleClickPlace}
        onRequestClose={addressDialog.actions.dismiss}
      />
    </>
  );
}

function PatientSearchbar(props: Props & { notificationsNode: React.ReactNode }) {
  const searchPatientDialog = useOpen();

  return (
    <>
      <ScaffoldSearchbar
        placeholder={props.filteredPatient?.fullName ?? translate(STRINGS.SEARCH_PATIENT)}
        trailing={props.notificationsNode}
        onClick={searchPatientDialog.actions.open}
        onFocus={searchPatientDialog.actions.open}
      />

      <SearchPatientDialog
        patientsState={props.patientsState}
        isOpen={searchPatientDialog.isOpen}
        placeholder={translate(STRINGS.SEARCH_PATIENT)}
        onClickPatient={(patient) => {
          props.onSearchPatient(patient);
          searchPatientDialog.actions.dismiss();
        }}
        onRequestClose={() => {
          searchPatientDialog.actions.dismiss();
          props.onSearchPatient(null);
        }}
      />
    </>
  );
}

const NavbarActionButtons = styled.div`
  display: flex;
`;

const Root = styled.div`
  background-color: black;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.08);
  padding: 16px;
  display: flex;
  gap: 12px;
  border-radius: 0;
`;

const Avatar = styled(AsyncImage)`
  background-color: var(--tw-slate-300);
  --size: 48px;
  width: var(--size);
  height: var(--size);
  background-size: cover;
  border-radius: var(--size);
  flex-shrink: 0;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

MainNavbar.Root = Root;
